import React from "react";

export default function ImageJobLoadingPlaceholder({
  status,
  message,
  size,
  position,
  progress,
  elapsedMs,
  onCancel = null,
  isCancelling = false,
}) {
  let resolvedMessage = "Generating image...";

  if (typeof message === "string" && message.trim()) {
    resolvedMessage = message.trim();
  } else if (status === "received") {
    resolvedMessage = "Processing image...";
  } else if (status === "pending") {
    resolvedMessage = "Generating image...";
  }

  const containerStyle = {
    width: size?.width ? `${size.width}px` : "100%",
    height: size?.height ? `${size.height}px` : "100%",
    left: position?.x ? `${position.x}px` : undefined,
    top: position?.y ? `${position.y}px` : undefined,
  };

  const pct = typeof progress === "number" && Number.isFinite(progress)
    ? Math.max(0, Math.min(100, progress))
    : null;

  const elapsedSeconds = typeof elapsedMs === "number" && Number.isFinite(elapsedMs)
    ? Math.max(0, Math.floor(elapsedMs / 1000))
    : null;

  let elapsedLabel = null;
  if (elapsedSeconds !== null) {
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;
    if (hours > 0) {
      elapsedLabel = `${hours}h ${String(minutes).padStart(2, "0")}m`;
    } else if (minutes > 0) {
      elapsedLabel = `${minutes}m ${seconds}s`;
    } else {
      elapsedLabel = `${seconds}s`;
    }
  }

  const canCancel = typeof onCancel === 'function' && typeof elapsedMs === 'number' && Number.isFinite(elapsedMs) && elapsedMs >= 3 * 60 * 1000;

  return (
    <div
      className="w-full h-full flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg"
      style={containerStyle}
    >
      <div className="w-[85%] max-w-sm border border-gray-200 rounded-lg bg-white shadow-sm p-4">
        <div className="flex items-center gap-3">
          <div
            className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-indigo-500 animate-spin"
            aria-hidden="true"
          />
          <div className="text-sm font-medium text-gray-800">{resolvedMessage}</div>
        </div>

        {elapsedLabel && (
          <div className="mt-1 text-[11px] text-gray-500">Generating time: {elapsedLabel}</div>
        )}

        {canCancel && (
          <div className="mt-3 flex flex-col items-start gap-2">
            <div className="mb-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
              Generation is taking too long
            </div>
            <button
              type="button"
              disabled={isCancelling}
              className={`inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                isCancelling ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isCancelling) onCancel();
              }}
            >
              {isCancelling && (
                <span
                  className="mr-2 h-3 w-3 rounded-full border-2 border-gray-300 border-t-indigo-500 animate-spin"
                  aria-hidden="true"
                />
              )}
              {isCancelling ? 'Cancelling...' : 'Cancel Generation'}
            </button>
          </div>
        )}

        {pct !== null && (
          <div className="mt-3">
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1 text-[11px] text-gray-500">{pct}%</div>
          </div>
        )}

        <div className="mt-3 text-[11px] text-gray-500">
          You can continue editing while the image is being generated.
        </div>
      </div>
    </div>
  );
}
